import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { REACT_APP_API_URL } from '../config';
import { apiFetch } from '../App';
import { getUserEmail } from '../utils/userUtils';
import './Manage.css';

function Manage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [models, setModels] = useState([]);
  const [clearOption, setClearOption] = useState('all');
  const [confirmClear, setConfirmClear] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [experimentComparisons, setExperimentComparisons] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResults();
    fetchExperiments();
    fetchModels();
  }, []);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get user email first
      try {
        const userEmail = await getUserEmail();
        const response = await apiFetch(`results?user_id=${encodeURIComponent(userEmail)}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch results: ${response.status}`);
        }
        
        const data = await response.json();
        setMetrics(data.filter(r => r.label_type === 'metric'));
        setComparisons(data.filter(r => r.label_type === 'comparison'));
      } catch (authError) {
        console.error('Auth error:', authError);
        setError('Authentication required. Please log in again.');
      }
    } catch (error) {
      setError(`Failed to load results: ${error.message}`);
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExperimentComparisons = async (experimentId) => {
    try {
      setLoading(true);
      const response = await apiFetch(`experiments/${experimentId}/comparisons`);
      
      if (response.ok) {
        const data = await response.json();
        setExperimentComparisons(data);
      } else {
        throw new Error('Failed to fetch experiment comparisons');
      }
    } catch (error) {
      setError(`Error fetching comparisons: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExperimentSelect = async (experiment) => {
    setSelectedExperiment(experiment);
    await fetchExperimentComparisons(experiment.id);
  };

  const fetchExperiments = async () => {
    try {
      const response = await apiFetch('experiments');

      if (response.ok) {
        const data = await response.json();
        setExperiments(data);
      } else {
        console.error('Failed to fetch experiments');
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await apiFetch('models');

      if (response.ok) {
        const data = await response.json();
        setModels(data);
      } else {
        console.error('Failed to fetch models');
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const handleClearData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user email
      try {
        const userEmail = await getUserEmail();
        const params = `?user=${encodeURIComponent(userEmail)}`;

        let endpoint = 'results';
        if (clearOption === 'comparisons') {
          endpoint = 'comparisons';
        } else if (clearOption === 'metrics') {
          endpoint = 'results/metrics';
        }

        const response = await apiFetch(`${endpoint}${params}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          throw new Error(`Failed to clear data: ${response.status}`);
        }

        // Refresh both results and experiments
        await Promise.all([
          fetchResults(),
          fetchExperiments()
        ]);
        setConfirmClear(false);
      } catch (authError) {
        console.error('Auth error:', authError);
        setError('Authentication required. Please log in again.');
      }
    } catch (error) {
      setError(`Failed to clear data: ${error.message}`);
      console.error('Error clearing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add loadImage function
  const loadImage = async (modelName, imageName) => {
    const key = `${modelName}-${imageName}`;
    
    try {
      setImageLoadingStates(prev => ({ ...prev, [key]: true }));
      
      const imagePath = `images/${modelName}/${imageName}`;
      const response = await apiFetch(imagePath);
      
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }
      
      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      setImageUrls(prev => ({ ...prev, [key]: imageUrl }));
      setImageLoadingStates(prev => ({ ...prev, [key]: false }));
      
      return imageUrl;
    } catch (error) {
      console.error('Error loading image:', error);
      setImageLoadingStates(prev => ({ ...prev, [key]: false }));
      return null;
    }
  };

  // Clean up object URLs when unmounting or when experiment changes
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [selectedExperiment]);

  // Load images when experiment comparisons change
  useEffect(() => {
    if (selectedExperiment && experimentComparisons.length > 0) {
      experimentComparisons.forEach(comparison => {
        loadImage(selectedExperiment.model_1, comparison.image_name);
        loadImage(selectedExperiment.model_2, comparison.image_name);
      });
    }
  }, [selectedExperiment, experimentComparisons]);

  const handleComparisonClick = (comparison) => {
    setSelectedComparison(comparison);
    setShowComparisonModal(true);
  };

  const handleCloseModal = () => {
    setShowComparisonModal(false);
    setSelectedComparison(null);
  };

  const renderComparisonModal = () => {
    if (!selectedComparison || !selectedExperiment) return null;

    return (
      <div className="modal-overlay" onClick={handleCloseModal}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Comparison Details</h3>
            <button className="close-button" onClick={handleCloseModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="modal-image-pair">
              <div className={`modal-image-container ${selectedComparison.result === selectedExperiment.model_1 ? 'chosen' : ''}`}>
                {imageLoadingStates[`${selectedExperiment.model_1}-${selectedComparison.image_name}`] && (
                  <div className="image-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading image...</span>
                  </div>
                )}
                {imageUrls[`${selectedExperiment.model_1}-${selectedComparison.image_name}`] && (
                  <img 
                    src={imageUrls[`${selectedExperiment.model_1}-${selectedComparison.image_name}`]}
                    alt={`Image from ${selectedExperiment.model_1}`}
                  />
                )}
                <div className="image-label">{selectedExperiment.model_1}</div>
              </div>
              <div className={`modal-image-container ${selectedComparison.result === selectedExperiment.model_2 ? 'chosen' : ''}`}>
                {imageLoadingStates[`${selectedExperiment.model_2}-${selectedComparison.image_name}`] && (
                  <div className="image-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading image...</span>
                  </div>
                )}
                {imageUrls[`${selectedExperiment.model_2}-${selectedComparison.image_name}`] && (
                  <img 
                    src={imageUrls[`${selectedExperiment.model_2}-${selectedComparison.image_name}`]}
                    alt={`Image from ${selectedExperiment.model_2}`}
                  />
                )}
                <div className="image-label">{selectedExperiment.model_2}</div>
              </div>
            </div>
            <div className="modal-comparison-info">
              <div className="comparison-result">
                <strong>Current Result:</strong> {selectedComparison.result}
              </div>
              <div className="comparison-metadata">
                <p><strong>Image Name:</strong> {selectedComparison.image_name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderImageExplorer = () => {
    if (!selectedExperiment || !experimentComparisons.length) {
      return null;
    }

    return (
      <div className="image-explorer">
        <h3>Comparisons Explorer: {selectedExperiment.name}</h3>
        <div className="comparison-grid">
          {experimentComparisons.map((comparison, index) => (
            <div 
              key={index} 
              className="comparison-card"
              onClick={() => handleComparisonClick(comparison)}
            >
              <div className="image-pair">
                <div className={`image-container ${comparison.result === selectedExperiment.model_1 ? 'chosen' : ''}`}>
                  {imageLoadingStates[`${selectedExperiment.model_1}-${comparison.image_name}`] && (
                    <div className="image-loading">
                      <div className="loading-spinner"></div>
                      <span>Loading image...</span>
                    </div>
                  )}
                  {imageUrls[`${selectedExperiment.model_1}-${comparison.image_name}`] && (
                    <img 
                      src={imageUrls[`${selectedExperiment.model_1}-${comparison.image_name}`]}
                      alt={`Image from ${selectedExperiment.model_1}`}
                    />
                  )}
                  <div className="image-label">{selectedExperiment.model_1}</div>
                </div>
                <div className={`image-container ${comparison.result === selectedExperiment.model_2 ? 'chosen' : ''}`}>
                  {imageLoadingStates[`${selectedExperiment.model_2}-${comparison.image_name}`] && (
                    <div className="image-loading">
                      <div className="loading-spinner"></div>
                      <span>Loading image...</span>
                    </div>
                  )}
                  {imageUrls[`${selectedExperiment.model_2}-${comparison.image_name}`] && (
                    <img 
                      src={imageUrls[`${selectedExperiment.model_2}-${comparison.image_name}`]}
                      alt={`Image from ${selectedExperiment.model_2}`}
                    />
                  )}
                  <div className="image-label">{selectedExperiment.model_2}</div>
                </div>
              </div>
              <div className="comparison-result">
                <strong>Result:</strong> {comparison.result}
              </div>
            </div>
          ))}
        </div>
        {renderComparisonModal()}
      </div>
    );
  };

  const renderCardMenu = (type, data) => {
    const handleDelete = async () => {
      try {
        setLoading(true);
        const userEmail = await getUserEmail();
        const params = `?user=${encodeURIComponent(userEmail)}`;
        
        let endpoint = 'results';
        if (type === 'comparisons') {
          endpoint = 'comparisons';
        } else if (type === 'metrics') {
          endpoint = 'results/metrics';
        }

        const response = await apiFetch(`${endpoint}${params}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          throw new Error(`Failed to delete ${type}: ${response.status}`);
        }

        // Refresh data
        await fetchResults();
      } catch (error) {
        setError(`Failed to delete ${type}: ${error.message}`);
        console.error('Error deleting data:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="card-menu">
        <button className="menu-button" onClick={handleDelete}>
          Delete {type}
        </button>
      </div>
    );
  };

  if (loading && (!experiments.length && !metrics.length && !comparisons.length)) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="manage-container">
      <h1>Database Management</h1>

      <div className="manage-header">
        <div className="stats">
          <div className="stat-box">
            <h3>Total Metrics</h3>
            <p>{metrics.length}</p>
          </div>
          <div className="stat-box">
            <h3>Total Comparisons</h3>
            <p>{comparisons.length}</p>
          </div>
          <div className="stat-box">
            <h3>Experiments</h3>
            <p>{experiments.length}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="experiments-section">
        <div className="section-header">
          <h2>Experiments</h2>
        </div>

        <div className="experiments-list">
          {experiments.map(experiment => (
            <div 
              key={experiment.id} 
              className={`experiment-card ${selectedExperiment?.id === experiment.id ? 'selected' : ''}`}
              onClick={() => handleExperimentSelect(experiment)}
            >
              <h3>{experiment.name}</h3>
              <p>Models: {experiment.model_1} vs {experiment.model_2}</p>
              <p>Status: {experiment.status}</p>
              <p>Progress: {experiment.completed} / {experiment.total} comparisons</p>
            </div>
          ))}
        </div>
      </div>

      {selectedExperiment && (
        <>
          {renderImageExplorer()}

          <div className="data-section">
            <div className="card">
              <div className="card-header">
                <h3>Metrics</h3>
              </div>
              <div className="data-list">
                {metrics.length > 0 ? (
                  metrics.map((metric, index) => (
                    <div key={index} className="data-item">
                      {`${metric.model_1}: ${metric.label} = ${metric.value}`}
                    </div>
                  ))
                ) : (
                  <p className="no-data">No metric data found</p>
                )}
              </div>
            </div>
          </div>

          <div className="clear-section">
            <h2>Clear Data</h2>
            <div className="clear-options">
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    value="all"
                    checked={clearOption === 'all'}
                    onChange={(e) => setClearOption(e.target.value)}
                  />
                  All Data
                  <span className="caption">Clear all database entries</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="comparisons"
                    checked={clearOption === 'comparisons'}
                    onChange={(e) => setClearOption(e.target.value)}
                  />
                  Only Comparisons
                  <span className="caption">Clear only image comparison results</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="metrics"
                    checked={clearOption === 'metrics'}
                    onChange={(e) => setClearOption(e.target.value)}
                  />
                  Only Metrics
                  <span className="caption">Clear only model metrics</span>
                </label>
              </div>

              <label className="confirm-checkbox">
                <input
                  type="checkbox"
                  checked={confirmClear}
                  onChange={(e) => setConfirmClear(e.target.checked)}
                />
                I understand this action cannot be undone
              </label>

              <button
                className="clear-button"
                onClick={handleClearData}
                disabled={!confirmClear}
              >
                Clear Selected Data
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Manage;